/**
 * Vincent B943 (Eric 2026-02-14 SPEC STRICK PROD) — Modal de vérification
 * factuelle de la synthèse via GPT-5.2.
 *
 * Flow :
 *   1. Vincent clique « Vérifier sur le web » dans SynthesisPanel.
 *   2. Le backend appelle GPT-5.2 avec un prompt fact-check et retourne
 *      une liste de claims potentiellement erronés (avec correction
 *      suggérée + source).
 *   3. Vincent coche les claims qu'il souhaite corriger.
 *   4. Boutons « Corriger synthèse » (scope=synthesis) ou « Corriger
 *      toutes connaissances » (scope=all_knowledge → Phase 2, disabled).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, MagnifyingGlass, Check, Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SynthesisVerifyModal({
  lang,
  videoId,
  synthesisId,
  isOpen,
  onClose,
  onApplied,
  onVerified,
}) {
  const [loading, setLoading] = useState(false);
  const [claims, setClaims] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(null); // "synthesis" | "all_knowledge"
  const [verifiedAt, setVerifiedAt] = useState(null);
  const [provider, setProvider] = useState(null);
  // Tâche 1319 (Eric 2026-06-30) — Quand le passage cité n'est plus dans la
  // synthèse (texte réécrit depuis la vérification), un simple toast rouge
  // disparaissait avant d'être lu et ne disait pas quoi faire. On garde le
  // refus À L'ÉCRAN, avec le bouton qui résout le problème.
  const [passageIntrouvable, setPassageIntrouvable] = useState(null);

  const runVerify = async () => {
    if (loading) return;
    setLoading(true);
    setClaims([]);
    setSelected(new Set());
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/synthesis/verify`,
        {},
        { withCredentials: true, timeout: 120_000 },
      );
      setClaims(data?.claims || []);
      setVerifiedAt(data?.verified_at || null);
      setProvider(data?.provider || null);
      const nClaims = Array.isArray(data?.claims) ? data.claims.length : 0;
      // Vincent B959 (Eric 2026-02-14 — Eric 2026-02-26 SPEC STRICK PROD)
      // — Signale au parent que la vérification est TERMINÉE pour flipper
      // le bouton « Vérifier sur le web » en check vert. Depuis Eric
      // 2026-02-26 : le check s'affiche même quand des erreurs SONT
      // détectées (le fait que le run ait tourné suffit). `hasClaims`
      // reste transmis à titre indicatif mais le parent ignore sa valeur.
      if (typeof onVerified === "function") {
        // Vincent B1021 (Eric 2026-02-26 SPEC STRICK PROD) — On transmet
        // aussi `claimsCount` pour permettre au parent (SynthesisPanel)
        // d'afficher un badge « N » à côté du check vert quand N erreurs
        // ont été détectées mais pas encore corrigées.
        onVerified({
          hasClaims: nClaims > 0,
          claimsCount: nClaims,
          verifiedAt: data?.verified_at || null,
        });
      }
      if (!data?.claims || data.claims.length === 0) {
        toast.success(
          lang === "fr"
            ? "Aucune erreur factuelle détectée par le vérificateur."
            : "No factual errors detected by the verifier.",
        );
      }
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
          (err?.response?.data?.detail || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleClaim = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyCorrections = async (scope) => {
    if (applying) return;
    const claimIds = Array.from(selected);
    if (claimIds.length === 0) {
      toast.error(lang === "fr" ? "Sélectionne au moins une correction." : "Select at least one correction.");
      return;
    }
    setApplying(scope);
    setPassageIntrouvable(null);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/synthesis/apply-corrections`,
        { claim_ids: claimIds, scope },
        { withCredentials: true },
      );
      const ts = data?.transcript_stats || {};
      const ks = data?.knowledge_stats || {};
      const nTranscripts = (ts.whisper_segments || 0) + (ts.fireflies_segments || 0) + (ts.merged_segments || 0);
      const nKnowledge = (ks.qa || 0) + (ks.concept || 0) + (ks.procedure || 0) + (ks.objection || 0) + (ks.script || 0);
      const knowledgeMsg = scope === "all_knowledge" && nKnowledge > 0
        ? (lang === "fr"
            ? ` ${nKnowledge} connaissance(s) mise(s) à jour (Q&R: ${ks.qa || 0}, Concepts: ${ks.concept || 0}, Procédures: ${ks.procedure || 0}, Objections: ${ks.objection || 0}, Scripts: ${ks.script || 0}).`
            : ` ${nKnowledge} knowledge item(s) updated.`)
        : "";
      toast.success(
        lang === "fr"
          ? `✓ ${data?.applied_count || 0} correction(s) appliquée(s). ${nTranscripts} segment(s) annoté(s).${knowledgeMsg}`
          : `✓ ${data?.applied_count || 0} correction(s) applied. ${nTranscripts} transcript segment(s) annotated.${knowledgeMsg}`,
      );
      // Vincent B1021 (Eric 2026-02-26 SPEC STRICK PROD) — Passe le
      // nombre de corrections appliquées ET le nombre de claims sélectionnés
      // au callback pour permettre au parent de décrémenter son badge
      // « N erreurs non corrigées ». `applied_count` peut différer si le
      // backend a filtré (ex : claim déjà rejeté).
      const appliedCount = Number(data?.applied_count || 0);
      if (onApplied) await onApplied({ appliedCount, selectedCount: claimIds.length });
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      // Le serveur nomme ce refus : la synthèse a été modifiée depuis la
      // vérification, donc les révisions ne s'ancrent plus nulle part.
      if (err?.response?.status === 400 && /n'est plus présent/.test(String(detail))) {
        setPassageIntrouvable(String(detail));
      } else {
        toast.error((lang === "fr" ? "Erreur : " : "Error: ") + detail);
      }
    } finally {
      setApplying(null);
    }
  };

  if (!isOpen) return null;

  const confidenceColor = (c) => {
    if (c >= 0.8) return "bg-red-100 text-red-800 border-red-300";
    if (c >= 0.5) return "bg-amber-100 text-amber-800 border-amber-300";
    return "bg-zinc-100 text-zinc-700 border-zinc-300";
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      data-testid="synthesis-verify-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between p-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <MagnifyingGlass size={20} className="text-blue-700" weight="bold" />
            <h2 className="text-lg font-semibold text-zinc-900">
              {lang === "fr" ? "Vérifier sur le web" : "Verify on web"}
            </h2>
            {verifiedAt && (
              <span className="text-[10px] text-zinc-500 ml-2" data-testid="synthesis-verify-timestamp">
                {lang === "fr" ? "vérifié : " : "verified: "}
                {new Date(verifiedAt).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
              </span>
            )}
            {provider && (
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ml-1 ${
                  provider === "perplexity-sonar-pro"
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                    : "bg-zinc-100 text-zinc-600 border border-zinc-300"
                }`}
                data-testid="synthesis-verify-provider-badge"
                title={provider === "perplexity-sonar-pro"
                  ? (lang === "fr" ? "Fact-check via Perplexity Sonar Pro (sources web temps réel)" : "Fact-check via Perplexity Sonar Pro (real-time web sources)")
                  : (lang === "fr" ? "Fact-check via GPT-5.2 (base de connaissances, pas de web live)" : "Fact-check via GPT-5.2 (knowledge base, no live web)")}
              >
                {provider === "perplexity-sonar-pro" ? "🌐 Sonar Pro" : "🧠 GPT-5.2"}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-zinc-100"
            data-testid="synthesis-verify-close-btn"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="p-4 flex-1 overflow-y-auto">
          {/* Tâche 1319 — Refus expliqué, avec l'action qui le résout. */}
          {passageIntrouvable && (
            <div
              className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-xs text-red-900"
              data-testid="synthesis-verify-stale-passage"
            >
              <p className="font-semibold mb-1">Correction impossible</p>
              <p className="mb-2">{passageIntrouvable}</p>
              <button
                type="button"
                onClick={() => { setPassageIntrouvable(null); runVerify(); }}
                disabled={loading}
                className="font-semibold px-3 py-1.5 rounded bg-red-700 text-white hover:bg-red-800 disabled:opacity-50"
                data-testid="synthesis-verify-rerun-after-stale-btn"
              >
                ↻ Relancer la vérification
              </button>
            </div>
          )}

          {/* Introduction / warning */}
          <div
            className="mb-4 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex gap-2"
            data-testid="synthesis-verify-disclaimer"
          >
            <Warning size={16} className="flex-shrink-0 mt-0.5" />
            <p>
              {provider === "perplexity-sonar-pro" ? (
                lang === "fr"
                  ? "Perplexity Sonar Pro identifie les affirmations factuelles potentiellement erronées avec des sources web temps réel. Vérifie chaque suggestion avec la source cliquable avant d'appliquer."
                  : "Perplexity Sonar Pro identifies potentially erroneous factual claims with real-time web sources. Verify each suggestion with the clickable source before applying."
              ) : (
                lang === "fr"
                  ? "GPT-5.2 identifie les affirmations factuelles potentiellement erronées (chiffres, dates, plafonds fiscaux, produits financiers). Sources non temps réel — configure Perplexity Sonar dans Forfait AI pour du fact-check web live."
                  : "GPT-5.2 identifies potentially erroneous factual claims (numbers, dates, tax thresholds). Sources not real-time — configure Perplexity Sonar in AI Package for live web fact-checking."
              )}
            </p>
          </div>

          {claims.length === 0 && !loading && (
            <div className="text-center py-8" data-testid="synthesis-verify-empty">
              <p className="text-sm text-zinc-500 mb-4">
                {verifiedAt
                  ? (lang === "fr" ? "✓ Aucune erreur factuelle détectée." : "✓ No factual errors detected.")
                  : (lang === "fr" ? "Lance la vérification pour analyser la synthèse." : "Run verification to analyze the synthesis.")}
              </p>
              <button
                type="button"
                onClick={runVerify}
                className="text-xs font-semibold px-4 py-2 rounded bg-blue-700 text-white hover:bg-blue-800"
                data-testid="synthesis-verify-run-btn"
              >
                {lang === "fr" ? "▶ Lancer la vérification" : "▶ Run verification"}
              </button>
            </div>
          )}

          {loading && (
            <div className="text-center py-8" data-testid="synthesis-verify-loading">
              <p className="text-sm text-zinc-600">
                {lang === "fr" ? "⏳ GPT-5.2 analyse la synthèse…" : "⏳ GPT-5.2 is analyzing the synthesis…"}
              </p>
            </div>
          )}

          {claims.length > 0 && !loading && (
            <ul className="space-y-3" data-testid="synthesis-verify-claims-list">
              {claims.map((c) => {
                const checked = selected.has(c.id);
                // Tâche 1323 (Eric 2026-06-30) — Le serveur a cherché la phrase
                // citée dans la synthèse au moment de la vérification. Si elle
                // n'a pas été retrouvée, la correction ne pourra PAS s'appliquer
                // (elle n'a pas de point d'ancrage) : on le dit ici au lieu de
                // laisser cliquer sur un bouton qui échouera. L'anomalie reste
                // affichée — l'erreur factuelle signalée est peut-être réelle.
                const introuvable = c.matched_text === null || c.matched_text === "";
                return (
                  <li
                    key={c.id}
                    className={`p-3 rounded border ${
                      introuvable ? "bg-amber-50 border-amber-300"
                        : checked ? "bg-blue-50 border-blue-300"
                          : "bg-white border-zinc-200"
                    }`}
                    data-testid={`synthesis-verify-claim-${c.id}`}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={introuvable}
                        onChange={() => toggleClaim(c.id)}
                        className="mt-1 h-4 w-4 accent-blue-700 disabled:opacity-40"
                        data-testid={`synthesis-verify-checkbox-${c.id}`}
                      />
                      <div className="flex-1 min-w-0 space-y-2">
                        {introuvable && (
                          <p
                            className="text-[11px] font-semibold text-amber-800"
                            data-testid={`synthesis-verify-unanchored-${c.id}`}
                          >
                            {lang === "fr"
                              ? "⚠ Passage non retrouvé dans la synthèse — à corriger à la main."
                              : "⚠ Passage not found in the synthesis — fix it manually."}
                          </p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${confidenceColor(c.confidence)}`}
                            title={lang === "fr" ? "Confiance dans la détection" : "Detection confidence"}
                          >
                            {Math.round((c.confidence || 0) * 100)}%
                          </span>
                          {c.issue && (
                            <span className="text-[11px] font-medium text-zinc-700">{c.issue}</span>
                          )}
                        </div>
                        <div className="text-xs">
                          <p className="text-zinc-500 mb-0.5">
                            {lang === "fr" ? "Extrait actuel :" : "Current text:"}
                          </p>
                          <p className="text-red-800 bg-red-50 rounded px-2 py-1 border border-red-200">
                            {c.original_text}
                          </p>
                        </div>
                        <div className="text-xs">
                          <p className="text-zinc-500 mb-0.5">
                            {lang === "fr" ? "Correction suggérée :" : "Suggested correction:"}
                          </p>
                          <p className="text-emerald-800 bg-emerald-50 rounded px-2 py-1 border border-emerald-200">
                            <Check size={12} className="inline mr-1" weight="bold" />
                            {c.suggested_correction}
                          </p>
                        </div>
                        {c.source && (
                          <p className="text-[11px] text-zinc-600">
                            <span className="font-semibold">
                              {lang === "fr" ? "Source : " : "Source: "}
                            </span>
                            {/* Vincent B943 Phase 3 (Eric 2026-02-14) — Source
                                cliquable si c'est une URL (Perplexity retourne
                                de vraies URL sources). */}
                            {/^https?:\/\//.test(c.source) ? (
                              <a
                                href={c.source}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-700 hover:underline break-all"
                                data-testid={`synthesis-verify-source-link-${c.id}`}
                              >
                                {c.source}
                              </a>
                            ) : (
                              c.source
                            )}
                          </p>
                        )}
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="p-4 border-t border-zinc-200 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-zinc-500">
            {selected.size > 0 && (
              <span data-testid="synthesis-verify-selected-count">
                {selected.size} {lang === "fr" ? "sélectionnée(s)" : "selected"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {claims.length > 0 && (
              <button
                type="button"
                onClick={runVerify}
                disabled={loading}
                className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                data-testid="synthesis-verify-rerun-btn"
              >
                {lang === "fr" ? "↻ Reprendre" : "↻ Re-run"}
              </button>
            )}
            <button
              type="button"
              onClick={() => applyCorrections("synthesis")}
              disabled={selected.size === 0 || !!applying}
              className="text-xs font-semibold px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
              data-testid="synthesis-verify-apply-synthesis-btn"
            >
              {applying === "synthesis"
                ? (lang === "fr" ? "⏳ Correction…" : "⏳ Applying…")
                : (lang === "fr" ? "Corriger synthèse" : "Correct synthesis")}
            </button>
            <button
              type="button"
              onClick={() => applyCorrections("all_knowledge")}
              disabled={selected.size === 0 || !!applying}
              title={lang === "fr"
                ? "Applique les corrections à la synthèse + Q&R + Concepts + Procédures + Objections + Scripts liés à cette vidéo."
                : "Applies corrections to synthesis + Q&A + Concepts + Procedures + Objections + Scripts linked to this video."}
              className="text-xs font-semibold px-3 py-1.5 rounded bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"
              data-testid="synthesis-verify-apply-all-btn"
            >
              {applying === "all_knowledge"
                ? (lang === "fr" ? "⏳ Correction…" : "⏳ Applying…")
                : (lang === "fr" ? "Corriger toutes connaissances" : "Correct all knowledge")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
